import { useState, useRef, useCallback, useEffect } from 'react';
import { MapPin, Loader2, Navigation } from 'lucide-react';
import type { LngLat } from '@duty/shared';
import { MAPBOX_TOKEN } from '@/config/mapbox';

export interface LocationResult {
  place: string;
  coordinates: LngLat;
}

interface MapboxFeature {
  place_name: string;
  center: [number, number];
}

function parseCoordinates(text: string): LngLat | null {
  // Match lat,lng — e.g. "-35.2558, 149.4434" or "-35.2558 149.4434"
  const m = text.trim().match(/^(-?\d{1,3}\.?\d*)[,\s]+(-?\d{1,3}\.?\d*)$/);
  if (!m) return null;
  const a = parseFloat(m[1]!);
  const b = parseFloat(m[2]!);
  if (isNaN(a) || isNaN(b)) return null;
  // Heuristic: if first value is in [-90,90] and second in [-180,180] treat as lat,lng
  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return { lat: a, lng: b };
  return null;
}

/**
 * Geocoding search input backed by the Mapbox Geocoding API.
 * Supports: street addresses, suburb/place names, decimal lat,lng coordinates.
 * Falls back to free-text if the geocoding API is unavailable.
 */
export function LocationSearch({
  value,
  onChange,
  placeholder = 'Enter address, suburb or lat,lng…',
  autoFocus,
}: {
  value: string;
  onChange: (result: LocationResult | string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<LocationResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const suppressBlurRef = useRef(false);

  // Re-sync the visible query when the parent resets/sets `value` externally
  // (e.g. a paste-box or another control populating the form) — `query` is
  // otherwise only ever driven by this component's own typing/selection.
  useEffect(() => {
    setQuery(value);
  }, [value]);

  const search = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) { setSuggestions([]); return; }

    // Try coordinate parsing first
    const coords = parseCoordinates(trimmed);
    if (coords) {
      setSuggestions([{ place: trimmed, coordinates: coords }]);
      return;
    }

    if (!MAPBOX_TOKEN) { setSuggestions([]); return; }

    setLoading(true);
    try {
      const url = [
        'https://api.mapbox.com/geocoding/v5/mapbox.places/',
        encodeURIComponent(trimmed),
        '.json?access_token=',
        MAPBOX_TOKEN,
        '&country=au,nz&types=address,place,locality,neighborhood,postcode&limit=5&language=en',
      ].join('');
      const res = await fetch(url);
      if (!res.ok) { setSuggestions([]); return; }
      const data = (await res.json()) as { features: MapboxFeature[] };
      setSuggestions(
        (data.features ?? []).map((f) => ({
          place: f.place_name,
          coordinates: { lng: f.center[0], lat: f.center[1] },
        })),
      );
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (q: string) => {
    setQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void search(q), 300);
  };

  const handleSelect = (result: LocationResult) => {
    suppressBlurRef.current = true;
    setQuery(result.place);
    setSuggestions([]);
    onChange(result);
    setTimeout(() => { suppressBlurRef.current = false; }, 0);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (suppressBlurRef.current) return;
      setSuggestions([]);
      if (query.trim()) onChange(query.trim());
    }, 150);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          autoFocus={autoFocus}
          aria-label="Location search"
          aria-autocomplete="list"
          aria-expanded={suggestions.length > 0}
          className="w-full rounded-md border border-border bg-surface-2 px-3 py-1.5 pr-8 text-sm placeholder:text-muted focus:border-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
        />
        <span className="pointer-events-none absolute right-2.5 top-2 text-muted">
          {loading
            ? <Loader2 size={13} className="animate-spin" />
            : <Navigation size={13} />
          }
        </span>
      </div>

      {suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-border bg-surface shadow-lg"
        >
          {suggestions.map((s, i) => (
            <li key={i} role="option" aria-selected={false}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
                className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40"
              >
                <MapPin size={12} className="mt-0.5 shrink-0 text-accent" aria-hidden />
                <span className="min-w-0 break-words">{s.place}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
